import React, { useEffect, useState, useCallback } from 'react';
import { X, ShieldCheck, BadgeCheck, Undo2, Truck, PackageCheck, Ban, Mail, StickyNote, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, inr, dt, StatusPill } from './adminApi';

const Box = ({ title, children, testid }) => (
  <section className="rounded-lg border border-[var(--si-line)] p-4" data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-wider text-[var(--si-grey)] mb-2">{title}</p>{children}
  </section>
);

const Btn = ({ icon: Icon, children, kind = 'outline', ...p }) => (
  <button {...p} className={`si-btn si-btn-${kind} !py-2 !px-3 text-sm ${p.className || ''}`}><Icon className="w-4 h-4" />{children}</button>
);

const PaymentBox = ({ o, run }) => {
  const [pid, setPid] = useState('');
  const pending = o.payment_status === 'pending';
  return (
    <Box title="Payment" testid="od-payment">
      <p className="text-sm">Status: <b className="capitalize" data-testid="od-payment-status">{(o.payment_status || '').replaceAll('_', ' ')}</b>{o.payment_source && ` · via ${o.payment_source}`}</p>
      <p className="text-sm">Razorpay order: <span className="font-mono">{o.razorpay_order_id}</span></p>
      <p className="text-sm">Payment ID: <span className="font-mono" data-testid="od-payment-id">{o.razorpay_payment_id || '-'}</span>{o.paid_at && ` · paid ${dt(o.paid_at)}`}</p>
      {o.last_payment_check && <p className="text-xs mt-1 text-[var(--si-grey)]">Last Razorpay check {dt(o.last_payment_check.at)}: {o.last_payment_check.message || o.last_payment_check.status}</p>}
      {pending && (
        <div className="mt-3 space-y-2">
          <Btn icon={ShieldCheck} kind="primary" onClick={() => run(() => api.verifyPayment(o.id), true)} data-testid="od-verify-payment">Verify payment on Razorpay</Btn>
          <div className="flex gap-2">
            <input value={pid} onChange={(e) => setPid(e.target.value)} placeholder="Payment ID / UPI ref (received manually)" className="si-input !py-2" data-testid="od-manual-payment-id" />
            <Btn icon={BadgeCheck} disabled={!pid.trim()} onClick={() => window.confirm('Mark this order as PAID and send confirmation email?') && run(() => api.markPaid(o.id, pid, 'Marked paid manually'))} data-testid="od-mark-paid">Mark paid</Btn>
          </div>
        </div>
      )}
    </Box>
  );
};

const RefundBox = ({ o, run }) => {
  const [amt, setAmt] = useState('');
  const [reason, setReason] = useState('');
  const remaining = o.total - (o.refunded_amount || 0);
  if (!['paid', 'partially_refunded'].includes(o.payment_status) && !(o.refunds || []).length) return null;
  const go = (manual) => {
    const a = amt ? Number(amt) : remaining;
    if (!window.confirm(`${manual ? 'Record a manual' : 'Send a Razorpay'} refund of ₹${a}?`)) return;
    run(() => api.refund(o.id, { amount: a, reason, manual }));
  };
  return (
    <Box title="Refunds" testid="od-refunds">
      {(o.refunds || []).map((r) => <p key={r.id} className="text-sm">{inr(r.amount)} · {dt(r.at)} · {r.manual ? 'manual' : r.id} {r.reason && `· ${r.reason}`}</p>)}
      {remaining > 0 && ['paid', 'partially_refunded'].includes(o.payment_status) && (
        <div className="mt-2 grid sm:grid-cols-[120px_1fr] gap-2">
          <input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder={`Max ${remaining}`} className="si-input !py-2" data-testid="od-refund-amount" />
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. wrong product delivered)" className="si-input !py-2" data-testid="od-refund-reason" />
          <div className="sm:col-span-2 flex flex-wrap gap-2">
            <Btn icon={Undo2} kind="buy" onClick={() => go(false)} data-testid="od-refund-razorpay">Refund via Razorpay</Btn>
            <Btn icon={Undo2} onClick={() => go(true)} data-testid="od-refund-manual">Record manual refund</Btn>
          </div>
        </div>
      )}
    </Box>
  );
};

const FulfilmentBox = ({ o, run }) => {
  const [ship, setShip] = useState({ courier: o.courier || '', tracking_number: o.tracking_number || '', tracking_url: o.tracking_url || '' });
  const s = o.status;
  const go = (status, extra = {}) => run(() => api.setStatus(o.id, { status, ...extra }));
  return (
    <Box title="Fulfilment" testid="od-fulfilment">
      <div className="flex items-center gap-2 mb-3"><span className="text-sm">Current:</span><StatusPill s={s} testid="od-status" /></div>
      {['confirmed', 'processing'].includes(s) && <Btn icon={PackageCheck} kind="primary" onClick={() => go('packed')} data-testid="od-mark-packed">Mark packed</Btn>}
      {['confirmed', 'processing', 'packed', 'shipped'].includes(s) && (
        <div className="mt-3 grid sm:grid-cols-3 gap-2">
          <input value={ship.courier} onChange={(e) => setShip({ ...ship, courier: e.target.value })} placeholder="Courier (e.g. Delhivery)" className="si-input !py-2" data-testid="od-courier" />
          <input value={ship.tracking_number} onChange={(e) => setShip({ ...ship, tracking_number: e.target.value })} placeholder="Tracking number" className="si-input !py-2" data-testid="od-tracking-number" />
          <input value={ship.tracking_url} onChange={(e) => setShip({ ...ship, tracking_url: e.target.value })} placeholder="Tracking link (optional)" className="si-input !py-2" data-testid="od-tracking-url" />
          {s !== 'shipped' && <Btn icon={Truck} kind="primary" className="sm:col-span-3 justify-self-start" disabled={!ship.courier || !ship.tracking_number} onClick={() => go('shipped', ship)} data-testid="od-mark-shipped">Mark shipped &amp; email customer</Btn>}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {s === 'shipped' && <Btn icon={CheckCircle2} kind="cart" onClick={() => go('delivered')} data-testid="od-mark-delivered">Mark delivered &amp; email customer</Btn>}
        {['pending_payment', 'confirmed', 'processing', 'packed'].includes(s) && <Btn icon={Ban} onClick={() => window.confirm('Cancel this order? Stock will be returned.') && go('cancelled')} data-testid="od-cancel">Cancel order</Btn>}
      </div>
      {o.courier && <p className="mt-2 text-sm">Shipped with <b>{o.courier}</b> · {o.tracking_number}</p>}
    </Box>
  );
};

const NotesBox = ({ o, run }) => {
  const [t, setT] = useState('');
  return (
    <Box title="Notes, emails & timeline" testid="od-notes">
      {(o.admin_notes || []).map((n, i) => <p key={i} className="text-sm bg-[#FFFBEB] rounded px-2 py-1 mb-1">{n.text} <span className="text-xs text-[var(--si-grey)]">· {dt(n.at)}</span></p>)}
      <div className="flex gap-2 mt-1">
        <input value={t} onChange={(e) => setT(e.target.value)} placeholder="Add an internal note" className="si-input !py-2" data-testid="od-note-input" />
        <Btn icon={StickyNote} disabled={!t.trim()} onClick={() => { run(() => api.addNote(o.id, t)); setT(''); }} data-testid="od-note-add">Add</Btn>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {['confirmed', 'shipped', 'delivered'].map((k) => <Btn key={k} icon={Mail} onClick={() => run(() => api.resendEmail(o.id, k))} data-testid={`od-resend-${k}`}>Resend {k} email</Btn>)}
      </div>
      {(o.emails || []).length > 0 && <p className="text-xs text-[var(--si-grey)] mt-2">Emails: {o.emails.map((e) => `${e.kind}${e.ok ? '' : ' (failed)'}`).join(', ')}</p>}
      <ol className="mt-3 border-l-2 border-[var(--si-line)] pl-4 space-y-2" data-testid="od-timeline">
        {[...(o.history || [])].reverse().map((h, i) => (
          <li key={i} className="text-sm"><StatusPill s={h.status} /> <span className="text-xs text-[var(--si-grey)]">{dt(h.at)} · {h.by}</span>{h.note && <p className="text-xs">{h.note}</p>}</li>
        ))}
      </ol>
    </Box>
  );
};

export const OrderDrawer = ({ id, onClose, onChanged }) => {
  const [o, setO] = useState(null);
  const load = useCallback(async () => setO(await api.order(id)), [id]);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, showMsg) => {
    try {
      const r = await fn();
      if (showMsg) toast[r.confirmed || r.already_paid ? 'success' : 'error'](r.message);
      else toast.success('Done');
      await load(); onChanged();
    } catch (e) { toast.error(e.message); }
  };
  const a = o?.shipping_address || {};
  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="order-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-3xl h-full overflow-y-auto bg-white shadow-2xl si-rise">
        <div className="sticky top-0 bg-[var(--si-navy)] text-white px-5 py-4 flex items-center justify-between z-10">
          <p className="font-bold text-lg">Order #{id.slice(0, 8).toUpperCase()}</p>
          <button onClick={onClose} aria-label="Close" data-testid="order-drawer-close"><X className="w-6 h-6" /></button>
        </div>
        {!o ? <p className="p-8">Loading…</p> : (
          <div className="p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Box title="Customer" testid="od-customer">
                <p className="font-semibold">{o.customer_name}</p>
                <p className="text-sm">{o.customer_phone}<br />{o.customer_email}</p>
                <p className="text-xs mt-1 text-[var(--si-grey)]">{o.customer_paid_orders} paid order(s) {o.customer_paid_orders > 1 && <b className="text-[#7E22CE]">· REPEAT CUSTOMER</b>}</p>
              </Box>
              <Box title="Ship to" testid="od-address">
                <p className="text-sm">{a.line1}{a.line2 && `, ${a.line2}`}<br />{a.city}, {a.state} {a.pincode}<br />{a.country}</p>
                <p className="text-xs mt-1 text-[var(--si-grey)]">Placed {dt(o.created_at)}</p>
              </Box>
            </div>
            <Box title="Items" testid="od-items">
              {o.items.map((it) => (
                <div key={`${it.product_slug}-${it.size}-${it.color}`} className="flex items-center gap-3 py-2 border-b border-[var(--si-line)] last:border-0 text-sm">
                  <img src={it.image} alt="" className="w-10 h-12 object-cover rounded" />
                  <span className="flex-1">{it.name}<br /><span className="text-xs text-[var(--si-grey)]">{[it.size, it.color].filter(Boolean).join(' · ')} · Qty {it.qty} × {inr(it.price)}</span></span>
                  <b>{inr(it.line_total)}</b>
                </div>
              ))}
              <div className="text-sm mt-2 space-y-0.5">
                <p className="flex justify-between"><span>Subtotal</span><span>{inr(o.subtotal)}</span></p>
                {(o.offers || []).map((of) => <p key={of.offer} className="flex justify-between text-[var(--si-green)]"><span>{of.label}</span><span>−{inr(of.amount)}</span></p>)}
                {o.discount_amount > 0 && <p className="flex justify-between text-[var(--si-green)]"><span>Code {o.discount_code}</span><span>−{inr(o.discount_amount)}</span></p>}
                <p className="flex justify-between"><span>Delivery</span><span>{o.shipping ? inr(o.shipping) : 'FREE'}</span></p>
                <p className="flex justify-between font-bold text-base"><span>Total</span><span data-testid="od-total">{inr(o.total)}</span></p>
              </div>
            </Box>
            <PaymentBox o={o} run={run} />
            <FulfilmentBox o={o} run={run} />
            <RefundBox o={o} run={run} />
            <NotesBox o={o} run={run} />
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDrawer;
